import type {
  HeadingProps,
  IntrinsicSystemProps,
  MotionPropsFor,
} from '@hover/blueprint';
import { Heading, forwardRef, motion, useTheme } from '@hover/blueprint';
import React from 'react';

type MotionHeadingProps = MotionPropsFor<HeadingProps>;

export interface TextOverlayProps
  extends MotionPropsFor<IntrinsicSystemProps<'div'>> {
  /**
   * The maximum width of the entire loading text container. This can be set to
   * prevent the text from overflowing when the image the text is overlaid onto
   * changes in size due to layout animations, etc.
   */
  textWidth?: MotionHeadingProps['maxWidth'];
  headerColor?: MotionHeadingProps['color'];
}

const MotionHeading = motion(Heading);

export const TextOverlay = forwardRef<TextOverlayProps, 'div'>(
  ({ children, textWidth, headerColor, ...props }, ref) => {
    const { breakpoints } = useTheme();

    return (
      <motion.div
        alignItems={{ base: 'center', tablet: 'start' }}
        bottom={{ tablet: 0 }}
        boxSizing="border-box"
        display="flex"
        flexDirection="row"
        height={{ base: '100%', tablet: 'unset' }}
        initial={false}
        justifyContent={{ base: 'center', tablet: 'start' }}
        layout
        paddingBottom={{ base: 0, tablet: 200 }}
        paddingX={{ base: 0, tablet: 300 }}
        pointerEvents="none"
        sx={{ containerType: 'inline-size' }}
        {...props}
        ref={ref}
      >
        <MotionHeading
          boxSizing="border-box"
          color={headerColor ?? 'neutral.0'}
          maxWidth={textWidth ?? '100%'}
          noMargin
          px={{ base: 200, tablet: 0 }}
          size={{ base: 500, tablet: 'xl' }}
          sx={{
            [`@media (min-width: ${breakpoints.tablet})`]: {
              '@container (width < 450px)': {
                textStyle: 'heading600',
              },
            },
          }}
          textAlign={{ base: 'center', tablet: 'left' }}
        >
          {children}
        </MotionHeading>
      </motion.div>
    );
  },
);
